import React from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Box,
  Skeleton,
  Divider,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import logStore from "../../stores/LogStore";
import PropTypes from "prop-types";
import deviceStore from "../../stores/DeviceStore";

const Label = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ color: "text.secondary", letterSpacing: 0.2 }}
  >
    {children}
  </Typography>
);

const Value = ({ children, sx }) => (
  <Typography variant="body2" sx={{ fontWeight: 600, ...(sx || {}) }}>
    {children}
  </Typography>
);

const DeviceCard = ({ device, loading = false }) => {
  const navigate = useNavigate();

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this device?")) {
      deviceStore.deleteDevice(device.id);
    }
  };

  const handleCardClick = () => {
    if (device?.pimios_id) {
      navigate(`/devices/${device.pimios_id}-${device.name}`);
    }
  };

  const isConnected =
    !loading && logStore.connectedDeviceIds.has(device?.pimios_id);

  return (
    <Box
      onClick={loading ? undefined : handleCardClick}
      sx={{
        cursor: loading ? "default" : "pointer",
        height: "100%",
        display: "flex",
      }}
    >
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          overflow: "hidden",
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Media */}
        {loading ? (
          <Skeleton variant="rectangular" height={140} />
        ) : (
          <CardMedia
            component="img"
            height="140"
            image={
              device.image_url ||
              device.type_image ||
              "https://placehold.co/150"
            }
            alt={device.name || "Device image"}
          />
        )}

        {/* Content */}
        <CardContent sx={{ p: 2.25, flexGrow: 1 }}>
          {/* Name */}
          <Box sx={{ mb: 1.5 }}>
            {loading ? (
              <Skeleton width="70%" height={28} />
            ) : (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {device.name || "Unnamed Device"}
              </Typography>
            )}
          </Box>

          {/* Device ID */}
          <Box sx={{ mb: 1.5 }}>
            {loading ? (
              <Skeleton width={180} />
            ) : (
              <>
                <Label>Device ID</Label>
                <Value
                  sx={{
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                  }}
                >
                  {device.pimios_id || "—"}
                </Value>
              </>
            )}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Details Grid */}
          <Grid container spacing={1.5}>
            {/* Type */}
            <Grid item xs={12} sm={6}>
              {loading ? (
                <>
                  <Skeleton width="45%" />
                  <Skeleton width="92%" />
                </>
              ) : (
                <>
                  <Label>Type</Label>
                  <Value>
                    {device.manufacture
                      ? `${device.manufacture} - ${device.type_name}`
                      : device.type_name || "—"}
                  </Value>
                </>
              )}
            </Grid>

            {/* Region */}
            <Grid item xs={12} sm={6}>
              {loading ? (
                <>
                  <Skeleton width="40%" />
                  <Skeleton width="80%" />
                </>
              ) : (
                <>
                  <Label>Region</Label>
                  <Value>{device.region_name || "—"}</Value>
                </>
              )}
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              {loading ? (
                <>
                  <Skeleton width="32%" />
                  <Skeleton width="100%" />
                </>
              ) : (
                <>
                  <Label>Location</Label>
                  <Value
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {device.location
                      ? [
                          device.location.street,
                          device.location.city,
                          device.location.state,
                        ]
                          .filter(Boolean)
                          .join(", ")
                      : "—"}
                  </Value>
                </>
              )}
            </Grid>
          </Grid>

          {/* Connection status */}
          <Box sx={{ mt: 1.5 }}>
            {loading ? (
              <>
                <Skeleton variant="text" width={50} height={20} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <Skeleton variant="circular" width={10} height={10} />
                  <Skeleton width={120} />
                </Box>
              </>
            ) : (
              <>
                <Label>Status</Label>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: isConnected ? "#22C55E" : "#EF4444",
                    }}
                  />
                  <Typography variant="body2">
                    {isConnected ? "Connected" : "Not Connected"}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </CardContent>

        {/* Actions pinned to bottom */}
        {!loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: 1.25,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              sx={{
                color: "#22C55E",
                "&:hover": { backgroundColor: "#BBF7D0" },
              }}
              onClick={() => navigate(`/edit-unit/${device.id}`)}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              sx={{
                backgroundColor: "#F3F4F6",
                color: "#6B7280",
                "&:hover": { backgroundColor: "#E5E7EB" },
              }}
              onClick={handleDelete}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Card>
    </Box>
  );
};

Label.propTypes = {
  children: PropTypes.node.isRequired,
};

Value.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

DeviceCard.propTypes = {
  device: PropTypes.shape({
    id: PropTypes.number,
    pimios_id: PropTypes.string,
    name: PropTypes.string,
    image_url: PropTypes.string,
    type_id: PropTypes.number,
    location_id: PropTypes.number,
    type_name: PropTypes.string,
    manufacture: PropTypes.string,
    type_image: PropTypes.string,
    location: PropTypes.shape({
      street: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
    }),
    region_name: PropTypes.string,
  }),
  loading: PropTypes.bool,
};

export default DeviceCard;
